import { Link } from '@tanstack/react-router';
import { X } from 'lucide-react';
import { useMemo } from 'react';

import { Button } from '@apps/web/components/ui/button';
import { Skeleton } from '@apps/web/components/ui/skeleton';
import { usePinnedTrainingRuns } from '@apps/web/features/teach_phoebe/model/use_pinned_training_runs';
import { RunRow } from '@apps/web/features/teach_phoebe/ui/run_row';
import { groupRunsByScheduler } from '@apps/web/lib/training_run_utils';
import {
    useOrganizationTrainingNeedsReview,
    useUser,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';

const MAX_VISIBLE_RUNS = 5;

export function ReviewMyRunsCard({
    organizationId,
    onDismiss,
}: {
    readonly organizationId: string | null;
    readonly onDismiss: () => void;
}) {
    const { data, isLoading, hasRequestFailed, mutate } =
        useOrganizationTrainingNeedsReview(
            organizationId,
            'chat-review-my-runs',
        );
    const { data: currentUser } = useUser({ reason: 'chat-review-my-runs' });
    const {
        reviewingRuns,
        visibleRuns,
        handleReviewingChange,
        handleFeedbackSaved,
    } = usePinnedTrainingRuns({
        runs: data?.runs ?? [],
        mutate,
    });
    const ownRuns = useMemo(() => {
        const ownGroup = groupRunsByScheduler(
            visibleRuns,
            currentUser?.id,
        ).find((group) => group.isOwn);
        return ownGroup?.runs ?? [];
    }, [visibleRuns, currentUser?.id]);
    const shownBeforePin = ownRuns.slice(0, MAX_VISIBLE_RUNS);
    const shownIds = new Set(shownBeforePin.map((run) => run.id));
    // A pinned run must render even if newer activity pushed it past the
    // top-N cutoff above — otherwise typing a note out of view unmounts it.
    const pinnedRuns = [...reviewingRuns.values()].filter(
        (run) => !shownIds.has(run.id),
    );
    const shownRuns = [...shownBeforePin, ...pinnedRuns];
    const remainingCount = ownRuns.length - shownRuns.length;

    return (
        <div className="bg-card mx-4 mb-2 rounded-lg border shadow-sm">
            <div className="flex items-center justify-between border-b px-3 py-2.5">
                <h3 className="text-sm font-semibold">Review chats</h3>
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={onDismiss}
                    aria-label="Dismiss"
                >
                    <X className="h-3.5 w-3.5" />
                </Button>
            </div>
            {isLoading ? (
                <div className="space-y-2 p-3">
                    <Skeleton className="h-9 w-full" />
                    <Skeleton className="h-9 w-full" />
                </div>
            ) : hasRequestFailed && !data ? (
                // Error is branched before emptiness on purpose: a failed
                // fetch must never render as "nothing to review".
                <div className="px-3 py-6 text-center">
                    <p className="text-sm font-medium">
                        Couldn’t load chats needing review
                    </p>
                    <Button
                        variant="outline"
                        size="sm"
                        className="mt-2"
                        onClick={() => void mutate()}
                    >
                        Try again
                    </Button>
                </div>
            ) : shownRuns.length === 0 ? (
                <div className="px-3 py-6 text-center">
                    <p className="text-sm font-medium">
                        No chats of yours need review right now
                    </p>
                    <p className="text-muted-foreground mt-0.5 text-xs">
                        Phoebe keeps learning from everything your team teaches
                        her.
                    </p>
                </div>
            ) : (
                // Rows carry no horizontal padding of their own since the
                // Teach Phoebe queue went flat; inset them here instead.
                <ul className="px-3">
                    {shownRuns.map((run, index) => (
                        <RunRow
                            key={run.id}
                            run={run}
                            isLast={index === shownRuns.length - 1}
                            organizationId={organizationId}
                            onFeedbackSaved={handleFeedbackSaved}
                            onReviewingChange={handleReviewingChange}
                        />
                    ))}
                </ul>
            )}
            <div className="border-t px-3 py-2 text-center">
                <Link
                    to="/teach-phoebe"
                    className="text-muted-foreground hover:text-foreground text-xs font-medium hover:underline"
                >
                    {remainingCount > 0
                        ? `+${remainingCount} more — view all in Teach Phoebe`
                        : 'View all in Teach Phoebe'}
                </Link>
            </div>
        </div>
    );
}
